import { useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { Bold, Heading2, ImagePlus, Italic, List, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/lib/useApi";
import { errMsg } from "@/lib/api";

const toHtml = (text) => (text && !text.includes("<") ? text.split(/\n{2,}/).map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`).join("") : text || "");

const ToolButton = ({ on, active, label, testId, children }) => (
  <button type="button" title={label} data-testid={testId} onClick={on}
    className={`rounded p-1.5 ${active ? "bg-amber-100 text-amber-900" : "text-stone-600 hover:bg-stone-100"}`}>{children}</button>
);

export const RichTextEditor = ({ value, onChange }) => {
  const call = useApi();
  const editor = useEditor({
    extensions: [StarterKit, Image],
    content: toHtml(value),
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
    editorProps: { attributes: { class: "prose-craft min-h-[160px] px-3 py-2 text-sm outline-none", "data-testid": "product-form-description" } },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML() && !editor.isFocused) editor.commands.setContent(toHtml(value));
  }, [value, editor]);

  const addImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const fd = new FormData(); fd.append("file", f);
      const { url } = await call("post", "/admin/upload", fd);
      editor.chain().focus().setImage({ src: url, alt: f.name }).run();
    } catch (err) { toast.error(errMsg(err, "Upload gagal")); } finally { e.target.value = ""; }
  };

  if (!editor) return null;
  return (
    <div className="rounded-md border border-input bg-white">
      <div className="flex flex-wrap gap-1 border-b border-stone-200 p-1">
        <ToolButton label="Tebal" testId="rte-bold" active={editor.isActive("bold")} on={() => editor.chain().focus().toggleBold().run()}><Bold className="h-4 w-4" /></ToolButton>
        <ToolButton label="Miring" testId="rte-italic" active={editor.isActive("italic")} on={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></ToolButton>
        <ToolButton label="Judul" testId="rte-heading" active={editor.isActive("heading", { level: 2 })} on={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></ToolButton>
        <ToolButton label="Daftar" testId="rte-bullet" active={editor.isActive("bulletList")} on={() => editor.chain().focus().toggleBulletList().run()}><List className="h-4 w-4" /></ToolButton>
        <ToolButton label="Daftar bernomor" testId="rte-ordered" active={editor.isActive("orderedList")} on={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></ToolButton>
        <label title="Sisipkan gambar" className="cursor-pointer rounded p-1.5 text-stone-600 hover:bg-stone-100">
          <ImagePlus className="h-4 w-4" />
          <input type="file" accept="image/*" className="hidden" onChange={addImage} data-testid="rte-image-upload" />
        </label>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};
